<template>
  <div>
    <step-intro iframe-url="">
        <p>This online form will guide you step-by-step in preparing your Word or LaTeX document for a professional, print-ready thesis—no advanced technical skills needed. You’re in control of the design, with the freedom to create a look that fits your style. And if you ever need help or have any questions, you can <a href="https://www.persoonlijkproefschrift.nl/contact/" target="_blank">contact our coordinator</a>. Simply follow the instructions in each step of the form, and you’ll soon have a polished manuscript that’s ready to be printed. Filling out this form and adjusting your Word document accordingly will take around  1½ hours. <b>Let’s get started!</b></p>
        <br>
        <p>The first step is to decide which additional design elements you'd like to include in your thesis. For these elements, we have a number of different templates available. Download the PDF below to explore our templates. Select your preferred template and choose which elements you’d like to include in your PDF layout.</p>
    </step-intro>
    <validation-observer tag="div" :step-index="stepIndex">
        <box title="Template and Elements">
            <p>To view our selection of templates, please download our PDF by clicking the button below. It is possible to match the font of the template-elements with the font of your thesis.</p>
            <a href="https://www.persoonlijkproefschrift.nl/diy-pakket/Templates.pdf" target="_blank" class="button is-primary">Download the PDF with templates</a>
            <br><br>
            <div class="columns is-vcentered">
                <div class="column is-narrow has-text-grey-dark">My selected template:</div>
                <div class="column">
                    <validation-provider rules="required" v-slot="{ errors }">
                        <b-field :message="errors" :type="{ 'is-danger': errors[0] }">
                            <b-select placeholder="Select a template" v-model="selectedTemplate">
                                <option v-for="option in templateOptions" :value="option.id" :key="option.id">
                                    {{ option.label }}
                                </option>
                            </b-select>
                        </b-field>
                    </validation-provider>
                </div>
            </div>
            <p>I would like to include the following elements:</p>
            <div class="field field--small">
                <b-checkbox @input="maybeUpdateTabs" v-model="headerAndPageNumbers">Headers and page numbers <a @click="headersAndPageNumberModal = true" href="#">View example</a>
                </b-checkbox>
            </div>
            <div class="field field--small">
                <b-checkbox @input="maybeUpdateTabs" v-model="tabs">Tabs <a @click="tabsModal = true" href="#">View example</a>
                </b-checkbox>
            </div>
            <div class="field field--small">
                <b-checkbox @input="maybeUpdateTabs" v-model="chapterPages">Chapter pages <a @click="chapterPagesModal = true" href="#">View example</a>
                </b-checkbox>
            </div>
        </box>
    </validation-observer>
    <b-modal :active.sync="headersAndPageNumberModal" :width="800">
        <div class="card">
            <div class="card-image">
                <img src="@/assets/headers-and-pagenumbers.jpg">
            </div>
            <div class="card-content">
                <div class="content">
                    <p>When choosing this element, headers will be placed on the top of every left and right page. You can customize headers for each chapter and appendix. In Step 5, you can specify your preferred headings.</p>
                </div>
            </div>
        </div>
    </b-modal>
    <b-modal :active.sync="tabsModal" :width="800">
        <div class="card">
            <div class="card-image">
                <img src="@/assets/tabs.jpg">
            </div>
            <div class="card-content">
                <div class="content">
                    <p>When choosing this element, <i>thumb tabs</i> will be placed on the right side of the page. Each chapter will have a numbered tab, while appendices will be marked with an ‘A’. No additional input is required from you except for the structure in Step 3.</p>
                </div>
            </div>
        </div>
    </b-modal>
    <b-modal :active.sync="chapterPagesModal" :width="800">
        <div class="card">
            <div class="card-image">
                <img src="@/assets/chapter-pages.jpg">
            </div>
            <div class="card-content">
                <div class="content">
                    <p>When choosing this element, the two chapter pages in your PDF will be replaced with the selected template design. In Step 5, you can input the chapter titles, authors, and publication details. If you have chapter pages that were designer by you or an external illustrator, you can leave this option unchecked. In step 2, you can upload the files for these chapter designs. You do not need to upload them if you have enlisted one of our own designers.</p>
                </div>
            </div>
        </div>
    </b-modal>
  </div>
</template>

<script>
import { mapFields } from 'vuex-map-fields';
import box from './../components/box.vue';
import stepIntro from './../components/step-intro.vue';
import ValidationObserver from './../components/validation-observer.vue';

export default {
    props: ['stepIndex'],
    components: {
        box,
        stepIntro,
        ValidationObserver
    },
    computed: {
        ...mapFields(['steps', 'templateOptions.selectedTemplate', 'templateOptions.elements.headerAndPageNumbers', 'templateOptions.elements.tabs', 'templateOptions.elements.chapterPages']),
    },
    data() {
        return {
            headersAndPageNumberModal: false,
            tabsModal: false,
            chapterPagesModal: false,
            templateOptions: [
                { id: '', label: 'Select a template' },
                { id: 'template-1', label: 'Template 1' },
                { id: 'template-2', label: 'Template 2' },
                { id: 'template-3', label: 'Template 3' },
                { id: 'template-4', label: 'Template 4' },
                { id: 'template-5', label: 'Template 5' },
                { id: 'template-6', label: 'Template 6' },
                { id: 'template-7', label: 'Template 7' }
            ]
        };
    },
    methods: {
        maybeUpdateTabs() {
            const onlyTabsSelected = this.tabs && !this.headerAndPageNumbers && !this.chapterPages;
            const detailsStep = this.steps.find(step => step.type === 'thesisDetails');
            if (detailsStep) {
                detailsStep.visible = !onlyTabsSelected;
            }
        }
    }
};
</script>

<style>
</style>
